import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatCurrencyAbbreviated, formatAxisDate, formatTooltipPoint } from './formatters';
import LineChartSkeleton from './LineChartSkeleton';
import { t } from '../../i18n/i18n';
import './LineChart.scss';

/**
 * @typedef {{ x: string | number, y: number }} LineChartPoint
 */

const STROKE_WIDTH = 2;
const MARKER_RADIUS = 5;
const Y_DOMAIN_HEADROOM = 0.08;
const DEFAULT_MAX_X_TICKS = 12;
const MIN_X_TICK_SPACING = 84;
/** One shared empty default, so the geometry memo isn't busted every render. */
const EMPTY_REFERENCES = [];

/**
 * One reusable chart for every line/area/sparkline usage in the Style Guide
 * (green PnL curves, blue balance curves, red mini sparklines). Visuals
 * (colour, axes, tooltip, gridlines) are all props — nothing about a given
 * usage is a separate component. `variant="sparkline"` short-circuits axes,
 * tooltip and gridlines regardless of the individual booleans, for the
 * embedded chart-only usage (table rows, cards).
 *
 * @param {{
 *   data: LineChartPoint[],
 *   lineColor?: string,
 *   accentColor?: string,
 *   guideColor?: string,
 *   gradientFrom?: string,
 *   gradientTo?: string,
 *   curve?: 'linear' | 'smooth',
 *   niceScale?: boolean,
 *   showXAxis?: boolean,
 *   showYAxis?: boolean,
 *   yAxisFormatter?: (value: number) => string,
 *   xAxisFormatter?: (value: string | number) => string,
 *   yAxisTickCount?: number,
 *   maxXTicks?: number,
 *   showGridLines?: boolean,
 *   showTooltip?: boolean,
 *   tooltipPosition?: 'auto' | 'top' | 'top-right',
 *   tooltipFormatter?: (point: LineChartPoint) => string,
 *   highlightedIndex?: number | null,
 *   showGuideLine?: boolean,
 *   height?: number,
 *   width?: number | string,
 *   variant?: 'full' | 'sparkline',
 *   emptyMessage?: string,
 *   referenceLines?: { value: number, color: string, label?: string }[],
 *   className?: string,
 * }} props
 *
 * - `curve` — `'linear'` (default) draws straight segments; pass `'smooth'`
 *   for the old Catmull-Rom spline (control points are clamped to each
 *   segment's local y-range so the curve can no longer overshoot past the
 *   true min/max — good for sparse series, still wrong for dense volatile
 *   ones, which is why straight segments are now the default).
 * - `niceScale` — `false` (default) keeps the old padded-domain Y ticks.
 *   `true` snaps the domain to round tick values (`niceTicks`) instead.
 * - `accentColor` — colours the hover marker; defaults to `lineColor` so
 *   existing callers are unaffected. The tooltip pill keeps its own default
 *   (`--grad-tooltip`, blue regardless of line colour — see index.scss) and
 *   only switches to `accentColor` when a caller explicitly sets it.
 * - `guideColor` — the dashed hover guide line's colour; defaults to a
 *   neutral `var(--text-muted)` instead of matching `lineColor`.
 * - `maxXTicks` — upper bound on X-axis label count; the actual count is
 *   derived from the plot's pixel width so labels don't crowd on narrow
 *   containers.
 * - `emptyMessage` — shown centred, axes/gridlines suppressed, when `data`
 *   is empty.
 * - `referenceLines` — horizontal levels drawn dashed across the plot (a
 *   profit target, a loss floor), each labelled at its right end. The Y
 *   domain stretches to take them in, so a level the curve hasn't reached yet
 *   is still on the chart rather than clipped off it.
 */
export default function LineChart({
  loading = false,
  data = [],
  lineColor = '#1A5DFD',
  accentColor: accentColorProp,
  guideColor = 'var(--text-muted)',
  gradientFrom,
  gradientTo,
  curve = 'linear',
  niceScale = false,
  showXAxis = true,
  showYAxis = true,
  yAxisFormatter = formatCurrencyAbbreviated,
  xAxisFormatter = formatAxisDate,
  yAxisTickCount = 4,
  maxXTicks = DEFAULT_MAX_X_TICKS,
  showGridLines = true,
  showTooltip = true,
  tooltipPosition = 'auto',
  tooltipFormatter = formatTooltipPoint,
  highlightedIndex,
  showGuideLine,
  height = 220,
  width = '100%',
  variant = 'full',
  emptyMessage = 'No data for this period',
  referenceLines = EMPTY_REFERENCES,
  className = '',
}) {
  const gid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  // `designWidth` is captured once (the first measured size) and frozen —
  // the SVG viewBox and all geometry (paths, ticks, padding) are pinned to
  // it so the chart never re-lays-out on resize. `renderedWidth` tracks the
  // live on-screen size so CSS can scale that frozen design down as one
  // unit (via `aspect-ratio` on the container) on narrower screens, instead
  // of the old behaviour of recomputing geometry at the live width, which
  // squeezed points/labels together while text/strokes stayed fixed size.
  const [designWidth, setDesignWidth] = useState(0);
  const [renderedWidth, setRenderedWidth] = useState(0);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [announceIndex, setAnnounceIndex] = useState(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) {
        setRenderedWidth(rect.width);
        setDesignWidth((prev) => prev || rect.width);
      }
    };
    updateSize();
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const next = entry.contentRect.width;
      if (next > 0) {
        setRenderedWidth(next);
        setDesignWidth((prev) => prev || next);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  useEffect(() => {
    setHoverIndex(null);
  }, [data]);

  const isSparkline = variant === 'sparkline';
  const axesX = !isSparkline && showXAxis;
  const axesY = !isSparkline && showYAxis;
  const gridOn = !isSparkline && showGridLines;
  const tooltipOn = !isSparkline && showTooltip;
  const guideOn = !isSparkline && (showGuideLine ?? tooltipOn);
  const interactive = tooltipOn || guideOn;

  const n = data.length;
  const isEmpty = n === 0;
  const w = designWidth || (typeof width === 'number' ? width : 600);
  const measuredWidth =
    renderedWidth || (containerRef.current ? containerRef.current.getBoundingClientRect().width : 0);
  const baseWidth = designWidth || w;
  const scale = baseWidth > 0 && measuredWidth > 0 ? measuredWidth / baseWidth : 1;
  const accentColor = accentColorProp ?? lineColor;

  const geometry = useMemo(
    () =>
      buildGeometry({ data, w, height, axesX, axesY, interactive, curve, niceScale, yAxisTickCount, maxXTicks, referenceLines }),
    [data, w, height, axesX, axesY, interactive, curve, niceScale, yAxisTickCount, maxXTicks, referenceLines]
  );
  const {
    padLeft,
    padRight,
    padTop,
    padBottom,
    baselineY,
    plotWidth,
    points,
    linePath,
    areaPath,
    yTickList,
    xTickIndices,
    xAt,
    references,
  } = geometry;

  const defaultIndex = highlightedIndex === null ? -1 : highlightedIndex === undefined ? n - 1 : highlightedIndex;
  const rawIndex = hoverIndex !== null ? hoverIndex : defaultIndex;
  const activeIndex = n && rawIndex >= 0 ? clamp(rawIndex, 0, n - 1) : -1;
  const activePoint = activeIndex >= 0 ? data[activeIndex] : null;
  const activePixel = activeIndex >= 0 && points[activeIndex] && points[activeIndex].y !== null ? points[activeIndex] : null;

  const handlePointerMove = useCallback(
    (event) => {
      if (!interactive || n === 0) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const xView = (event.clientX - rect.left) * (w / rect.width) - padLeft;
      const ratio = plotWidth > 0 ? xView / plotWidth : 0;
      setHoverIndex(clamp(Math.round(ratio * (n - 1)), 0, n - 1));
    },
    [interactive, n, w, padLeft, plotWidth]
  );
  const handlePointerLeave = useCallback(() => setHoverIndex(null), []);
  const handleKeyDown = useCallback(
    (event) => {
      if (!interactive || n === 0) return;
      const base = activeIndex >= 0 ? activeIndex : 0;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const next = clamp(base - 1, 0, n - 1);
        setHoverIndex(next);
        setAnnounceIndex(next);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const next = clamp(base + 1, 0, n - 1);
        setHoverIndex(next);
        setAnnounceIndex(next);
      } else if (event.key === 'Home') {
        setHoverIndex(0);
        setAnnounceIndex(0);
      } else if (event.key === 'End') {
        setHoverIndex(n - 1);
        setAnnounceIndex(n - 1);
      } else if (event.key === 'Escape') {
        setHoverIndex(null);
        setAnnounceIndex(null);
      }
    },
    [interactive, n, activeIndex]
  );

  useLayoutEffect(() => {
    if (tooltipRef.current) setTooltipWidth(tooltipRef.current.offsetWidth);
  }, [activeIndex, activePoint, tooltipOn, w, loading]);

  let tooltipStyle = null;
  if (tooltipOn && activePixel && !loading) {
    if (tooltipPosition === 'top-right') {
      tooltipStyle = { top: 6, right: padRight * scale };
    } else {
      const currentWidth = measuredWidth || w;
      const half = tooltipWidth > 0 ? tooltipWidth / 2 : 0;
      const minLeft = padLeft * scale + half;
      const maxLeft = Math.max(minLeft, currentWidth - padRight * scale - half);
      const targetLeft = activePixel.x * scale;
      const left = clamp(targetLeft, minLeft, maxLeft);
      tooltipStyle = {
        left,
        top: activePixel.y * scale - 14,
        transform: 'translate(-50%, -100%)',
        // Hide until measured so the pill doesn't render unclamped for one frame.
        visibility: tooltipWidth > 0 ? 'visible' : 'hidden',
      };
    }
    // Tooltip keeps its own default (--grad-tooltip) unless a caller
    // explicitly asked for a different accent.
    if (accentColorProp != null) tooltipStyle.background = accentColorProp;
  }

  const fillFrom = gradientFrom || lineColor;
  const hasGradientTo = gradientTo != null && gradientTo !== 'transparent';

  const ariaLabel = useMemo(() => buildAriaLabel({ data, n, yAxisFormatter, xAxisFormatter }), [
    data,
    n,
    yAxisFormatter,
    xAxisFormatter,
  ]);
  const announcedPoint = announceIndex !== null ? data[announceIndex] : null;

  const chartSvg = (
    <svg
      className="ac-line-chart__svg"
      width="100%"
      height="100%"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      focusable="false"
    >
      <title>{ariaLabel}</title>
      {interactive && <desc>{t('Use arrow keys, Home and End to move between points, Escape to reset.')}</desc>}
      <defs>
        <linearGradient id={`${gid}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillFrom} stopOpacity="0.22" />
          <stop offset="100%" stopColor={hasGradientTo ? gradientTo : fillFrom} stopOpacity={hasGradientTo ? 1 : 0} />
        </linearGradient>
      </defs>

      {gridOn &&
        yTickList.map((tick, i) => (
          <line
            key={i}
            className="ac-line-chart__grid"
            x1={padLeft}
            x2={w - padRight}
            y1={tick.y}
            y2={tick.y}
          />
        ))}

      {references.map((ref) => (
        <g key={`${ref.label}-${ref.value}`} className="ac-line-chart__ref">
          <line x1={padLeft} x2={w - padRight} y1={ref.y} y2={ref.y} stroke={ref.color} />
          {ref.label && (
            <text x={w - padRight - 4} y={ref.y - 5} fill={ref.color}>
              {t(ref.label)}
            </text>
          )}
        </g>
      ))}

      {areaPath && <path d={areaPath} fill={`url(#${gid}-fill)`} stroke="none" />}
      {linePath && (
        <path
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {guideOn && activePixel && (
        <line
          className="ac-line-chart__guide"
          style={{ stroke: guideColor }}
          x1={activePixel.x}
          x2={activePixel.x}
          y1={padTop}
          y2={baselineY}
        />
      )}

      {interactive && activePixel && (
        <circle
          className="ac-line-chart__marker"
          cx={activePixel.x}
          cy={activePixel.y}
          r={MARKER_RADIUS}
          fill={accentColor}
        />
      )}

      {axesY &&
        yTickList.map((tick, i) => (
          <text key={`y-${i}`} className="ac-line-chart__y-label" x={padLeft - 10} y={tick.y}>
            {yAxisFormatter(tick.value)}
          </text>
        ))}

      {axesX &&
        xTickIndices.map((i, idx) => (
          <text
            key={`x-${i}`}
            className={`ac-line-chart__x-label ac-line-chart__x-label--${
              idx === 0 ? 'start' : idx === xTickIndices.length - 1 ? 'end' : 'middle'
            }`}
            x={xAt(i)}
            y={height - padBottom + 18}
          >
            {xAxisFormatter(data[i].x)}
          </text>
        ))}
    </svg>
  );

  return (
    <div
      ref={containerRef}
      className={`ac-line-chart ac-line-chart--${variant} ${className}`.trim()}
      // Once a design size is captured, drive height off `aspect-ratio`
      // instead of a literal pixel height so the whole chart scales down
      // as one unit when the container narrows. Before that first
      // measurement (one frame, synchronously resolved pre-paint above)
      // fall back to a literal height so it never collapses to 0.
      style={designWidth ? { width, aspectRatio: `${w} / ${height}` } : { width, height }}
    >
      {loading ? (
        <LineChartSkeleton
          height={height}
          width="100%"
          variant={variant}
          showGridLines={showGridLines}
          showAxes={variant !== 'sparkline' && (showXAxis || showYAxis)}
          className={className}
        />
      ) : isEmpty ? (
        <div className="ac-line-chart__empty">{t(emptyMessage)}</div>
      ) : (
        <>
          {interactive ? (
            <div
              className="ac-line-chart__interactive"
              role="group"
              aria-label={ariaLabel}
              tabIndex={0}
              onPointerMove={handlePointerMove}
              onPointerLeave={handlePointerLeave}
              onKeyDown={handleKeyDown}
            >
              {chartSvg}
            </div>
          ) : (
            chartSvg
          )}

          {tooltipStyle && (
            <div ref={tooltipRef} className="ac-line-chart__tooltip" style={tooltipStyle}>
              {tooltipFormatter(activePoint)}
            </div>
          )}

          <div className="ac-line-chart__sr" aria-live="polite">
            {announcedPoint ? tooltipFormatter(announcedPoint) : ''}
          </div>
        </>
      )}
    </div>
  );
}

function clamp(value, min, max) {
  if (min > max) [min, max] = [max, min];
  return Math.min(Math.max(value, min), max);
}

/** Evenly-spaced indices, capped at `max`, always including the first/last point. */
function pickTickIndices(n, max) {
  if (n <= 0) return [];
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => Math.round(i * step));
}

/** "Nice" round tick values spanning at least [min, max]. */
function niceTicks(min, max, count) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const raw = (max - min) / Math.max(count - 1, 1);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const start = Math.floor(min / step) * step;
  const steps = Math.ceil((max - start) / step);
  // index-multiply, don't accumulate — accumulation drifts on decimal steps
  return Array.from({ length: steps + 1 }, (_, i) => start + i * step);
}

/** Min/max over finite `y` values only — a `for` loop, not `Math.min(...ys)`, so it can't blow the call stack and a stray non-finite value can't NaN it. */
function finiteYExtent(data) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    const y = data[i]?.y;
    if (Number.isFinite(y)) {
      if (y < min) min = y;
      if (y > max) max = y;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  return { min, max };
}

/** Splits pixel-space points at non-finite `y` so one bad sample gaps the line instead of NaN-ing the whole path. */
function splitValidRuns(points) {
  const runs = [];
  let current = [];
  for (const p of points) {
    if (p.y === null) {
      if (current.length) runs.push(current);
      current = [];
    } else {
      current.push(p);
    }
  }
  if (current.length) runs.push(current);
  return runs;
}

function buildLinearPath(points) {
  if (points.length === 0) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
}

/** Catmull-Rom spline through `points`, expressed as cubic bezier segments. */
function buildSmoothPath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  if (points.length === 2) {
    return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;
  }
  const segments = [`M${points[0].x},${points[0].y}`];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    let cp1x = p1.x + (p2.x - p0.x) / 6;
    let cp1y = p1.y + (p2.y - p0.y) / 6;
    let cp2x = p2.x - (p3.x - p1.x) / 6;
    let cp2y = p2.y - (p3.y - p1.y) / 6;
    // Clamp each control point to its own segment's y-range so the spline
    // can't overshoot past the true local min/max on dense volatile series.
    const lo = Math.min(p1.y, p2.y);
    const hi = Math.max(p1.y, p2.y);
    cp1y = clamp(cp1y, lo, hi);
    cp2y = clamp(cp2y, lo, hi);
    segments.push(`C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`);
  }
  return segments.join(' ');
}

/** All layout/scale/path geometry — memoized separately from hover state so scrubbing only touches the marker/guide/tooltip, not this. */
function buildGeometry({
  data,
  w,
  height,
  axesX,
  axesY,
  interactive,
  curve,
  niceScale,
  yAxisTickCount,
  maxXTicks,
  referenceLines = EMPTY_REFERENCES,
}) {
  const n = data.length;
  const edgePad = STROKE_WIDTH + (interactive ? MARKER_RADIUS : 0);
  const padLeft = axesY ? 46 : edgePad;
  const padRight = edgePad;
  const padTop = edgePad;
  const padBottom = axesX ? 26 : edgePad;
  const plotWidth = Math.max(w - padLeft - padRight, 1);
  const plotHeight = Math.max(height - padTop - padBottom, 1);
  const baselineY = padTop + plotHeight;

  const levels = referenceLines.filter((ref) => Number.isFinite(ref?.value));
  const extent = finiteYExtent(data);
  // Stretched to take in the reference levels, so none is clipped off.
  const yMin = levels.reduce((lo, ref) => Math.min(lo, ref.value), extent.min);
  const yMax = levels.reduce((hi, ref) => Math.max(hi, ref.value), extent.max);
  const niceTickValues = niceScale ? niceTicks(yMin, yMax, Math.max(yAxisTickCount, 2)) : [];

  let domainMin;
  let domainMax;
  if (niceScale && niceTickValues.length) {
    domainMin = niceTickValues[0];
    domainMax = niceTickValues[niceTickValues.length - 1];
    if (domainMin === domainMax) domainMax = domainMin + 1;
  } else {
    let lo = yMin;
    let hi = yMax;
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const span = hi - lo;
    domainMin = lo - span * Y_DOMAIN_HEADROOM;
    domainMax = hi + span * Y_DOMAIN_HEADROOM;
  }

  const xAt = (i) => padLeft + (n > 1 ? (i / (n - 1)) * plotWidth : plotWidth / 2);
  const yAt = (value) => padTop + (1 - (value - domainMin) / (domainMax - domainMin)) * plotHeight;

  const points = data.map((d, i) => ({
    x: xAt(i),
    y: Number.isFinite(d?.y) ? yAt(d.y) : null,
  }));

  const runs = splitValidRuns(points);
  const buildPath = curve === 'smooth' ? buildSmoothPath : buildLinearPath;
  const linePath = runs.map((run) => buildPath(run)).join(' ');
  const areaPath = runs
    .map((run) => {
      if (!run.length) return '';
      const path = buildPath(run);
      const last = run[run.length - 1];
      const first = run[0];
      return `${path} L${last.x},${baselineY} L${first.x},${baselineY} Z`;
    })
    .filter(Boolean)
    .join(' ');

  const yTickValues = axesY
    ? niceScale
      ? niceTickValues
      : Array.from(
          { length: yAxisTickCount },
          (_, i) => domainMin + (i / Math.max(yAxisTickCount - 1, 1)) * (domainMax - domainMin)
        )
    : [];
  const yTickList = yTickValues.map((value) => ({ value, y: yAt(value) })).reverse();

  const xTickCount = axesX ? clamp(Math.floor(plotWidth / MIN_X_TICK_SPACING), 2, maxXTicks) : 0;
  const xTickIndices = axesX ? pickTickIndices(n, Math.min(xTickCount, Math.max(n, 1))) : [];

  const references = levels.map((ref) => ({ ...ref, y: yAt(ref.value) }));

  return {
    padLeft,
    padRight,
    padTop,
    padBottom,
    plotWidth,
    plotHeight,
    baselineY,
    points,
    linePath,
    areaPath,
    yTickList,
    xTickIndices,
    xAt,
    references,
  };
}

function buildAriaLabel({ data, n, yAxisFormatter, xAxisFormatter }) {
  if (!n) return t('Line chart, no data');
  const first = data[0];
  const last = data[n - 1];
  const { min, max } = finiteYExtent(data);
  return t('Line chart with {{count}} points, ranging from {{min}} to {{max}}, from {{start}} to {{end}}', {
    count: n,
    min: yAxisFormatter(min),
    max: yAxisFormatter(max),
    start: xAxisFormatter(first.x),
    end: xAxisFormatter(last.x),
  });
}

export { LineChartSkeleton };
